import React, { useEffect, useState, useRef, useMemo } from 'react'
import { unescape } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import Modal from '../../../Modal'
import CenteredLoader from '../../../view/CenteredLoader'
import { CloseWidgetIcon } from '../../../../icons/Icons'
import { initialIframeSetting } from '../../../../app/config'
import { openNewTab } from '../../../../helper/openNewTab'
import { getWebviewId } from '../../../../helper/getWebviewId'
import { wrapTextAddLinks } from '../../../../helper/wrapTextAddLinks'
import { getContentForWebviewModal } from '../../../../api/api'
import { sendUserEventToParent } from '../../../../actions/static'
import { focusTrapCreator } from '../../../../helper/focusTrapCreator'
import { keyType } from '../../../../constants/string'
import { palette } from '../../../../theme/palette'

import {
  CloseButtonContainer,
  ModalContainer,
  ModalHeader,
  ModalContent,
  WebViewText,
  ModalWrapper,
  WebViewImage,
  LoaderContainer
} from './WebviewModal.style'

const WebviewModal = props => {
  const { settings, isPanoramaPost, isMobile, isMaximized } = props
  const [webviewData, setWebviewData] = useState({})
  const isEmptyWebviewData = JSON.stringify(webviewData) === '{}'
  const textRef = useRef(null)
  const webViewModalRef = useRef(null)
  const closeWebViewButtonRef = useRef(null)
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!webviewData.text || !textRef) {
      return
    }
    closeWebViewButtonRef.current.focus()
    linkEditorListener()
  }, [webviewData.text])

  useEffect(() => {
    if (props.isUserIdle) {
      handleClose()
    }
  }, [props.isUserIdle])

  const linkEditorListener = () => {
    const links = textRef.current.querySelectorAll('.linkEdited')

    links.forEach(link => {
      const extension = JSON.parse(link.getAttribute('data-extension'))
      const url = link.getAttribute('data-href')
      if (extension) {
        link.onkeydown = (e) => {
          const currentKey = e.key.toLowerCase()
          if (currentKey === keyType.ENTER) {
            e.preventDefault()
            props.setIframe({
              iframeSrc: url,
              isIframeOpen: true,
              questionnaireName: props.iframeSettings.questionnaireName
            })
          }
        }
        link.onclick = e => {
          e.preventDefault()
          props.setIframe({
            iframeSrc: url,
            isIframeOpen: true,
            questionnaireName: props.iframeSettings.questionnaireName
          })
        }
      } else {
        link.onkeydown = (e) => {
          const currentKey = e.key.toLowerCase()
          if (currentKey === keyType.ENTER) {
            e.preventDefault()
            openNewTab(url)
          }
        }
        link.onclick = e => {
          e.preventDefault()
          openNewTab(url)
        }
      }
    })
  }

  useEffect(() => {
    if (!props.settings.botId || !props.iframeSettings.iframeSrc) {
      return
    }
    const webviewId = getWebviewId(props.iframeSettings.iframeSrc)
    getContentForWebviewModal(webviewId).then(response => {
      if (response) {
        setWebviewData(response)
        sendOpenEvent(response)
        closeWebViewButtonRef.current.focus()
      }
    })
  }, [props.settings])

  const handleClose = () => {
    if (!isEmptyWebviewData) {
      handleCloseIframe()
      sendCloseEvent(webviewData)
    }
  }

  const handleCloseIframe = () => {
    const copyIframeSettings = {
      ...initialIframeSetting,
      questionnaireName: props.iframeSettings.questionnaireName
    }

    props.setIframe(copyIframeSettings)
  }

  const sendOpenEvent = (webview) => {
    const eventOpen = webview?.eventInfo?.openEventName
    if (eventOpen) {
      sendUserEventToParent(eventOpen)
    }
  }

  const sendCloseEvent = (webview) => {
    const eventClose = webview?.eventInfo?.closeEventName
    if (eventClose) {
      sendUserEventToParent(eventClose)
    }
  }

  const imageLoadHandler = () => {
    setIsLoading(false)
  }

  const isLoadingData = useMemo(() => {
    return webviewData.imageUrl ? isLoading : isEmptyWebviewData
  }, [isEmptyWebviewData, isLoading, webviewData.imageUrl])

  return (
    <Modal
      isMobile={isMobile}
      isMaximized={isMaximized}
      onClose={handleClose}
    >
      <ModalWrapper
        aria-label={t('webview')}
        role='dialog'
        aria-modal='true'
        ref={webViewModalRef}
        onKeyDown={(e) => focusTrapCreator(e, webViewModalRef.current, true)}
      >
        <ModalContainer>
          <ModalHeader>
            <CloseButtonContainer
              ref={closeWebViewButtonRef}
              aria-label={t('webview_close')}
              tabIndex={0}
              onClick={handleClose}
            >
              <CloseWidgetIcon color={palette.secondaryText} />
            </CloseButtonContainer>
          </ModalHeader>

          <ModalContent isLoading={isLoadingData}>
            {webviewData.imageUrl && (
              <WebViewImage
                src={webviewData.imageUrl}
                alt={webviewData.imageAltText}
                isNeedMargin={!!webviewData.text}
                onLoad={imageLoadHandler}
              />
            )}
            {webviewData.text && (
              <WebViewText
                ref={textRef}
                role='textbox'
                dangerouslySetInnerHTML={{
                  __html: wrapTextAddLinks(unescape(webviewData.text), t('external_link_icon'))
                }}
              />
            )}
          </ModalContent>

          {isLoadingData &&
            <LoaderContainer>
              <CenteredLoader position={'relative'} size={48} borderTopColor={settings?.color} />
            </LoaderContainer>
          }
        </ModalContainer>
      </ModalWrapper>
    </Modal>
  )
}

export default WebviewModal
